import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../utils/api';
import { Bell, X, Check, CheckCheck, Clock, MessageSquare, Settings } from 'lucide-react';

const NOTIF_ROUTES = {
  task: '/tasks',
  leave: '/leave-management',
  overtime: '/leave-management',
  insurance_policy: '/insurance',
  reminder: '/reminders',
};

const NotificationBell = ({ user, dropdownAlign = 'right' }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const [notifs, countData] = await Promise.all([
        api.getNotifications(false, 10),
        api.getNotificationCount()
      ]);
      setNotifications(notifs);
      setUnreadCount(countData.unread_count);
    } catch (error) { toast.error("Something went wrong"); } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await api.markNotificationRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDismiss = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await api.dismissNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      toast.success('Notification dismissed');
    } catch (error) {
      toast.error('Failed to dismiss notification');
    }
  };

  const handleNotifClick = async (notif) => {
    // Mark as read
    if (!notif.is_read) {
      try { await api.markNotificationRead(notif.notification_id); } catch { toast.error("Something went wrong"); }
      setNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    // Navigate to relevant page
    const refType = notif.reference_type || '';
    const route = NOTIF_ROUTES[refType];
    if (route) {
      setIsOpen(false);
      navigate(route);
    } else {
      // Fallback: go to notifications page
      setIsOpen(false);
      navigate('/notifications');
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'task_deadline':
      case 'task_overdue':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'project_update':
        return <Settings className="w-4 h-4 text-blue-500" />;
      case 'whatsapp':
        return <MessageSquare className="w-4 h-4 text-green-500" />;
      default:
        return <Bell className="w-4 h-4 text-slate-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'border-l-red-500';
      case 'high': return 'border-l-orange-500';
      case 'normal': return 'border-l-blue-500';
      default: return 'border-l-slate-300';
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell">
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute ${dropdownAlign === 'left' ? 'left-0' : 'right-0'} mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-200 z-50`} data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-800">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-3 h-3" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto" data-testid="notification-list">
            {loading && notifications.length === 0 ? (
              <div className="p-4 text-center text-slate-500 text-sm">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No notifications</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.notification_id}
                  onClick={() => handleNotifClick(notif)}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 border-l-4 hover:bg-slate-50 cursor-pointer ${
                    !notif.is_read ? 'bg-blue-50/50' : ''
                  } ${getPriorityColor(notif.priority)}`}
                  data-testid={`notification-item-${notif.notification_id}`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {getIcon(notif.notification_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notif.is_read ? 'font-medium text-slate-800' : 'text-slate-700'}`}>
                      {notif.title}
                    </p>
                    <p className="text-xs text-slate-500 truncate mt-0.5">{notif.message}</p>
                    <p className="text-xs text-slate-400 mt-1">{formatTime(notif.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!notif.is_read && (
                      <button
                        onClick={(e) => handleMarkRead(notif.notification_id, e)}
                        className="p-1 hover:bg-slate-200 rounded"
                        title="Mark as read"
                      >
                        <Check className="w-3 h-3 text-slate-500" />
                      </button>
                    )}
                    <button
                      onClick={(e) => handleDismiss(notif.notification_id, e)}
                      className="p-1 hover:bg-slate-200 rounded"
                      title="Dismiss"
                    >
                      <X className="w-3 h-3 text-slate-500" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-100">
              <a
                href="/notifications"
                className="text-xs text-blue-600 hover:text-blue-700"
                data-testid="view-all-notifications"
              >
                View all notifications
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
